import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ThumbsUp, ThumbsDown } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import type { FeedbackType } from '../types'

interface Props {
  id: string
  initialFeedback?: FeedbackType
  onFeedback?: (id: string, feedback: FeedbackType) => void
  className?: string
}

/**
 * Subtle inline feedback - just two small icons that appear when needed
 * Non-intrusive: small, muted colors, no labels
 */
export default function InlineFeedback({ id, initialFeedback, onFeedback, className = '' }: Props) {
  const { t: tc } = useTranslation('common')
  const [feedback, setFeedback] = useState<FeedbackType>(initialFeedback ?? null)
  const [hasInteracted, setHasInteracted] = useState<boolean>(!!initialFeedback)

  const handleFeedback = (type: 'up' | 'down') => {
    const newFeedback: FeedbackType = feedback === type ? null : type
    setFeedback(newFeedback)
    setHasInteracted(true)
    onFeedback?.(id, newFeedback)
  }

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <button
        onClick={() => handleFeedback('up')}
        className={`p-1 rounded transition-all ${
          feedback === 'up'
            ? 'text-emerald-400'
            : 'text-pearl/45 hover:text-emerald-400/60'
        }`}
        title={tc('feedback.goodSuggestion')}
      >
        <ThumbsUp size={12} />
      </button>
      <button
        onClick={() => handleFeedback('down')}
        className={`p-1 rounded transition-all ${
          feedback === 'down'
            ? 'text-red-400'
            : 'text-pearl/45 hover:text-red-400/60'
        }`}
        title={tc('feedback.notRelevant')}
      >
        <ThumbsDown size={12} />
      </button>

      {/* Subtle thank you that fades */}
      <AnimatePresence>
        {hasInteracted && feedback && (
          <motion.span
            initial={{ opacity: 0, x: -5 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="text-xs text-pearl/50 ml-1"
          >
            {tc('feedback.noted')}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  )
}
